// frontend/utils/time.js
//
// Timezone helpers: every record we send to the server should carry the
// IANA zone the user was in when they entered the time, so that day-bucketing
// (e.g. "what did I do on Tuesday") stays correct after travel/DST.

/**
 * Returns the browser's IANA time zone (e.g. "Europe/Berlin"). Falls back to
 * "UTC" if the runtime can't resolve one.
 */
export function getDeviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Best-effort list of IANA zone names for a <select>. Uses
 * Intl.supportedValuesOf where available; otherwise a curated fallback.
 */
export function listSupportedTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch {
    // ignore
  }
  return [
    'UTC',
    'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Sao_Paulo',
    'Europe/London', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Rome', 'Europe/Athens', 'Africa/Cairo', 'Africa/Johannesburg',
    'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Singapore',
    'Australia/Sydney', 'Pacific/Auckland',
  ];
}

function timezoneOffsetMs(instant, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(instant)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  );
  const hour = parts.hour === '24' ? '00' : parts.hour;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - instant.getTime();
}

/**
 * Convert a "YYYY-MM-DDTHH:MM" wall-clock string interpreted in `timeZone`
 * to a UTC ISO 8601 string. When `timeZone` is omitted the browser's local
 * zone is used (matching `new Date(localStr).toISOString()`).
 */
export function wallClockToISO(localStr, timeZone) {
  if (!localStr) return null;
  if (!timeZone) return new Date(localStr).toISOString();
  const guess = new Date(`${localStr}Z`);
  const offset = timezoneOffsetMs(guess, timeZone);
  return new Date(guess.getTime() - offset).toISOString();
}

/**
 * Format a UTC ISO string / Date as "YYYY-MM-DDTHH:MM" wall-clock in the
 * given IANA zone (or the browser's local zone when omitted).
 */
export function isoToWallClock(value, timeZone) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  if (!timeZone) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value])
  );
  const hh = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hh}:${parts.minute}`;
}


/**
 * Formats seconds into human readable duration string with hours and minutes
 * @param {number} seconds - Total seconds to format
 * @returns {string} Formatted string like "1h 30m" or "5m 30s"
 */
export function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m ${remainingSeconds}s`;
  }
}

/**
 * Formats seconds into a compact non-ticking duration string.
 * Examples: "<1m", "12m", "1h 12m", "2h"
 * @param {number} seconds - Total seconds to format
 * @returns {string} Compact duration string without seconds
 */
export function formatCompactDuration(seconds) {
  const safeSeconds = Math.max(0, Math.floor(seconds || 0));

  if (safeSeconds < 60) {
    return '<1m';
  }

  const totalMinutes = Math.floor(safeSeconds / 60);

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${minutes}m`;
}

/**
 * Formats seconds into timecode format with optional negative values
 * @param {number} seconds - Total seconds to format
 * @returns {string} Formatted string like "1:30:45" or "-0:45"
 */
export function formatTimecode(seconds) {
  const isNegative = seconds < 0;
  seconds = Math.abs(seconds);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const remainingSeconds = (seconds % 60).toString().padStart(2, '0');

  const formattedTime = hours > 0 
    ? `${hours}:${minutes}:${remainingSeconds}`
    : `${minutes}:${remainingSeconds}`;

  return isNegative ? `-${formattedTime}` : formattedTime;
}

/**
 * Formats seconds into timecode with bold HTML tags for hours/minutes
 * @param {number} seconds - Total seconds to format
 * @returns {string} HTML string with bold tags like "<b>1</b>:<b>30</b>:45"
 */
export function formatBoldTimecode(seconds, showSign = false) {
  const isNegative = seconds < 0;
  seconds = Math.abs(seconds);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const remainingSeconds = (seconds % 60).toString().padStart(2, '0');

  const formattedTime = hours > 0 
    ? `<b>${hours}</b>:<b>${minutes}</b>:${remainingSeconds}`
    : `<b>${minutes}</b>:${remainingSeconds}`;

  if (!showSign) {
    return formattedTime;
  } else {
    return isNegative ? `-${formattedTime}` : `+${formattedTime}`;
  }
}

/**
 * Formats Date object into localized time string based on user's locale
 * @param {Date} date - Date object to format
 * @returns {string} Formatted time like "13:45" or "1:45 PM" depending on locale
 */
export function formatTime(date) {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: undefined // Let the system decide based on locale
  });
}


/**
 * Calculates the remaining time for a time block in seconds
 * @param {Object} block - The time block object
 * @param {Date} block.startDate - Start date of the block
 * @param {Date} block.endDate - End date of the block
 * @param {Date} block.activeEventStartDate - Start date of the current active event (can be null)
 * @param {number} [block.duration=0] - Previous accumulated duration in seconds
 * @returns {number} Remaining time in seconds. Positive values indicate overtime, negative values indicate time remaining
 */
export function calculateRemainingTime(block) {
  if (!block.endDate || !block.startDate) return 0;

  // Calculate total duration in seconds from start/end dates
  const totalDurationSeconds = 
    (block.endDate.getTime() - block.startDate.getTime()) / 1000;

  // Get current elapsed time if there's an active event
  const elapsedSeconds = block.activeEventStartDate ? Math.max(
    0,
    Math.floor(
      (new Date().getTime() - block.activeEventStartDate.getTime()) / 1000 + 1
    )
  ) : 0;

  // Add any previous duration
  const totalElapsed = elapsedSeconds + (block.duration || 0);

  // Calculate remaining time
  return totalDurationSeconds - totalElapsed ;
}

/**
 * Calculate duration in seconds between two dates
 * @param {Date} startDate - Start date and time
 * @param {Date} endDate - End date and time
 * @returns {number} Duration in seconds, or 0 if invalid dates
 */
export function calculateDurationSeconds(startDate, endDate) {
  // Handle invalid or missing dates
  if (!startDate || !endDate) {
    return 0;
  }

  // Ensure we have Date objects
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  return Math.max(0, (end.getTime() - start.getTime()) / 1000);
}


/**
 * Calculates the duration of a time block in seconds
 * @param {Object} block - The time block object
 * @param {Date} block.startDate - The start date of the block
 * @param {Date} block.endDate - The end date of the block
 * @returns {number} Duration in seconds
 */
export function calculateBlockDuration(block) {
  return calculateDurationSeconds(block.startDate, block.endDate);
}


/**
 * Formats the duration of a block into a human-readable string
 * @param {Object} block - The block object containing time information
 * @returns {string} The formatted duration string
 */
export function formatBlockDuration(block) {
  return formatDuration(calculateBlockDuration(block));
}

/**
 * Formats Date object into localized hour display format
 * @param {Date} date - Date object to format
 * @returns {string} Formatted time like "1:45 PM"
 */
export function formatLocalizedTime(date) {
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
}

/**
 * Formats remaining time with +/- indicators for overage/remaining
 * @param {number} remainingSeconds - Remaining time in seconds (positive means overage, negative means time left)
 * @returns {string} HTML string like "-<b>25</b>:30" (remaining) or "+<b>5</b>:15" (overage)
 */
export function formatRemainingTime(remainingSeconds) {
  const isOvertime = remainingSeconds > 0;
  const absSeconds = Math.abs(remainingSeconds);
  
  const minutes = Math.floor(absSeconds / 60);
  const seconds = (absSeconds % 60).toString().padStart(2, '0');
  
  const sign = isOvertime ? '+' : '-';
  return `${sign}<b>${minutes}</b>:${seconds}`;
}

/**
 * Formats a date into a full localized string
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date like "Monday, January 1, 2024"
 */
export function formatFullDate(date) {
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}